import { DateInput, TextInput, Dropdown, useTranslator, getLocale } from "@sia/style-guide";
import { useCallback, useContext, useEffect, useState } from "react";
import { WizarContext } from "contexts/WizardContext";
import { corporateInfoActionCreator } from "contexts/WizardContext";
import webPlatformAdapter from "common/Adapters/WebPlatformAdapter/WebPlatformAdapter";
import pgAdapter from "common/Adapters/PGAdapter/PGAdapter";
import GlobalContext from "contexts/GlobalContext";
import { initialWizardFormState } from "contexts/WizardContext";

const Info = ({ errors, setErrors }) => {
  const translate = useTranslator();
  const { language } = useContext(GlobalContext);
  const { state, dispatch } = useContext(WizarContext);
  let corporateInfo = state.corporate.corporateInfo;
  let corporateInfoErrors = errors.corporate.corporateInfo;
  let changState = useCallback((payload) => dispatch(corporateInfoActionCreator(payload)), [dispatch]);

  const resetInputFieldError = (property, error) => {
    const exculdedProperties = ["extraName"];
    if (!exculdedProperties.includes(property)) {
      setErrors((errorsState) => ({
        ...errorsState,
        corporate: {
          ...errorsState.corporate,
          corporateInfo: { ...errorsState.corporate.corporateInfo, [property]: error },
        },
      }));
    } else {
      exculdedProperties.forEach((exculdedProperty) => {
        if (property === exculdedProperty)
          setErrors((errorsState) => ({
            ...errorsState,
            corporate: {
              ...errorsState.corporate,
              corporateInfo: { ...errorsState.corporate.corporateInfo, [exculdedProperty]: error },
            },
          }));
      });
    }
  };

  const [branches, setBranches] = useState([]);
  const [activities, setActivities] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      const data_work_branches = await webPlatformAdapter.request("GET", "rpc/get_work_branches_list", { lang: getLocale(language) });
      let branches = data_work_branches.map((e) => ({ ...e, label: e.name }));
      setBranches(branches);
      const data_work_specialisation = await webPlatformAdapter.request("GET", "rpc/get_work_specialisation_list", { lang: getLocale(language) });
      let activities = data_work_specialisation.map((e) => ({ ...e, label: e.name }));
      setActivities(activities);
    };
    fetchData();
  }, [language]);

  const { user } = useContext(GlobalContext);
  const userId = user.siaId;

  useEffect(() => {
    if (userId) {
      pgAdapter.getWizardSession(({ data }) => {
        const defaultCorporateInfo = { ...initialWizardFormState.corporate.corporateInfo };
        const { corporateInfo } = data?.corporate || {};
        const { name, extraName, foundingDate, language, uid, website, branch, specialites } = corporateInfo || defaultCorporateInfo;
        changState({ name, extraName, foundingDate, language, uid, website, branch, specialites });
      });
    }
  }, [userId, changState]);

  const setUserSession = (obj) => {
    pgAdapter.getWizardSession(({ data }) => {
      const updatedSession = {
        ...data,
        corporate: { ...(data.corporate || {}), corporateInfo: { ...(data?.corporate?.corporateInfo || {}), ...obj } },
      };
      pgAdapter.setWizardSession({ ...updatedSession });
    });
  };

  const languages = [
    { label: "DE", id: "de" },
    { label: "FR", id: "fr" },
    { label: "IT", id: "it" },
    { label: "EN", id: "en" },
  ];

  return (
    <div className="corporate-info-page">
      <div className="sia-margin-03-top"></div>
      <TextInput
        onBlur={() => {
          setUserSession({ name: corporateInfo.name });
        }}
        id={"corporate-page-corporate-name"}
        value={corporateInfo.name}
        onChange={(event, value) => {
          resetInputFieldError("name");
          changState({ name: value });
        }}
        label={translate("wizard.form.corporate.info.name")}
        placeholder={translate("wizard.form.single.work-place.new.company.name.placeholder")}
        required={true}
        invalidPatternText={corporateInfoErrors.name}
        invalidText={corporateInfoErrors.name}
      />
      <TextInput
        onBlur={() => {
          setUserSession({ extraName: corporateInfo.extraName });
        }}
        id={"corporate-page-corporate-extraName"}
        value={corporateInfo.extraName}
        onChange={(event, value) => {
          resetInputFieldError("extraName");
          changState({ extraName: value });
        }}
        label={translate("wizard.form.corporate.info.extra-name")}
        placeholder={translate("wizard.form.single.work-place.new.company.name.placeholder")}
        invalidPatternText={corporateInfoErrors.extraName}
        invalidText={corporateInfoErrors.extraName}
      />
      <DateInput
        onBlur={() => {
          setUserSession({ foundingDate: corporateInfo.foundingDate });
        }}
        id={"corporate-page-corporate-foundingDate"}
        required={true}
        label={translate("wizard.form.corporate.info.founding-date")}
        placeholder={"01.01.2000"}
        value={corporateInfo.foundingDate}
        onChange={(event, value, error) => {
          resetInputFieldError("foundingDate", error);
          changState({ foundingDate: value });
        }}
        invalidPatternText={translate("form.input.error.invalid.with-example", { example: "d.m.yyyy" })}
        invalidText={corporateInfoErrors.foundingDate}
      />
      <Dropdown
        id={"corporate-page-language"}
        label={translate("wizard.form.single.personal-info.language.label")}
        items={languages}
        value={corporateInfo.language}
        required={true}
        onChange={(event, value) => {
          resetInputFieldError("language");
          changState({ language: value });
          setUserSession({ language: value });
        }}
        placeholder={translate("wizard.form.single.placeholder")}
        invalid={corporateInfoErrors.language}
        invalidText={corporateInfoErrors.language}
      />
      <TextInput
        onBlur={() => {
          if (corporateInfo.uid) {
            setUserSession({ uid: corporateInfo.uid });
          }
        }}
        id={"corporate-page-corporate-uid"}
        value={corporateInfo.uid}
        onChange={(event, value) => {
          resetInputFieldError("uid");
          changState({ uid: value });
        }}
        label={translate("wizard.form.corporate.info.uid")}
        placeholder={translate("wizard.form.corporate.info.uid.placeholder")}
        required={true}
        invalidPatternText={corporateInfoErrors.uid}
        invalidText={corporateInfoErrors.uid}
      />
      <TextInput
        onBlur={() => {
          setUserSession({ website: corporateInfo.website });
        }}
        id={"corporate-page-corporate-website"}
        value={corporateInfo.website}
        onChange={(event, value) => {
          resetInputFieldError("website");
          changState({ website: value });
        }}
        label={translate("wizard.form.corporate.info.website")}
        placeholder={translate("idp.register.corporate.website.placeholder")}
        invalidPatternText={corporateInfoErrors.website}
        invalidText={corporateInfoErrors.website}
      />

      <Dropdown
        id={"corporate-page-branch"}
        label={translate("wizard.form.corporate.info.branch")}
        items={branches}
        value={corporateInfo.branch}
        required={true}
        onChange={(event, value) => {
          resetInputFieldError("branch");
          changState({ branch: value });
          setUserSession({ branch: value });
        }}
        placeholder={translate("wizard.form.single.placeholder")}
        invalid={corporateInfoErrors.branch}
        invalidText={corporateInfoErrors.branch}
      />
      <Dropdown
        id={"corporate-page-activities"}
        label={translate("wizard.form.corporate.info.specialities")}
        items={activities}
        value={corporateInfo.specialites}
        required={true}
        onChange={(event, value) => {
          resetInputFieldError("specialites");
          changState({ specialites: value });
          setUserSession({ specialites: value });
        }}
        placeholder={translate("wizard.form.single.placeholder")}
        invalid={corporateInfoErrors.specialites}
        invalidText={corporateInfoErrors.specialites}
      />
    </div>
  );
};

export default Info;
